import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, ExternalLink, ShieldCheck } from 'lucide-react';
import { AppHeader } from '@/components/layout/AppHeader';
import { Footer } from '@/components/layout/Footer';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { submitVerificationRequest } from '@/lib/api/profiles';
import { useProfile } from '@/lib/hooks/useProfiles';
import { useAuth } from '@/lib/hooks/useAuth';

const schema = z.object({
  linkedin_url: z.string().min(1, 'LinkedIn URL is required').url('Enter a valid LinkedIn URL'),
  note: z.string().max(500).optional(),
  confirmed: z.boolean().refine((v) => v, 'You must confirm this is your profile.'),
});
type FormValues = z.infer<typeof schema>;

export function ClaimProfile() {
  const { handle } = useParams<{ handle: string }>();
  const { isAuthenticated, loaded } = useAuth();
  const { data: profile, isLoading } = useProfile(handle);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { confirmed: false, linkedin_url: '', note: '' },
  });

  const onSubmit = async (values: FormValues) => {
    if (!profile) return;
    setSubmitError(null);
    try {
      await submitVerificationRequest(values.linkedin_url, values.note || undefined, profile.id);
      setSubmitted(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Submission failed.';
      if (msg.includes('already_verified')) {
        setSubmitError('This profile is already verified.');
      } else if (msg.includes('pending_request_exists')) {
        setSubmitError(
          'A verification request is already pending for this account. Check your profile edit page for status.',
        );
      } else if (msg.includes('seed_already_claimed')) {
        setSubmitError('This profile has already been claimed by another member.');
      } else if (msg.includes('no_profile_found')) {
        setSubmitError('Please complete your profile setup before requesting verification.');
      } else {
        setSubmitError(msg);
      }
    }
  };

  if (!loaded || isLoading) {
    return (
      <Shell>
        <Skeleton className="mx-auto mt-16 h-96 max-w-lg rounded-2xl" />
      </Shell>
    );
  }

  if (!profile) {
    return (
      <Shell>
        <div className="container py-16 text-center">
          <h1 className="font-display text-3xl text-navy">Profile not found</h1>
          <Button asChild variant="gold" className="mt-6">
            <Link to="/directory">Back to directory</Link>
          </Button>
        </div>
      </Shell>
    );
  }

  if (profile.status !== 'seeded') {
    return (
      <Shell>
        <div className="container max-w-lg py-16 text-center">
          <h1 className="font-display text-2xl text-navy">
            This profile is not claimable
          </h1>
          <p className="mt-3 text-sm text-muted">
            Only unverified Facebook-seeded profiles can be claimed.
          </p>
          <Button asChild variant="gold" className="mt-6">
            <Link to={`/profile/${handle}`}>Back to profile</Link>
          </Button>
        </div>
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell>
        <div className="container max-w-lg py-16 flex flex-col gap-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/20">
                  <ShieldCheck className="h-5 w-5 text-gold-dark" aria-hidden="true" />
                </span>
                <CardTitle>Verification request submitted</CardTitle>
              </div>
              <CardDescription className="mt-2">
                We'll review your LinkedIn and original Facebook intro post. Typically reviewed
                within <strong>1–3 business days</strong>. You'll receive an in-app notification
                once a decision is made.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="text-base">While you wait</CardTitle>
              <CardDescription>
                Complete your profile so other members can find and connect with you.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="gold" className="w-full">
                <Link to="/me/edit">
                  Complete your profile
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="container max-w-lg py-12">
        {!isAuthenticated ? (
          <Card>
            <CardHeader>
              <CardTitle>Sign in to request verification</CardTitle>
              <CardDescription>
                You need a CPN account to verify ownership of{' '}
                <strong className="text-navy">{profile.display_name}</strong>'s profile.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Button asChild variant="gold">
                <Link
                  to="/signup"
                  state={{ from: { pathname: `/profile/${handle}/claim` } }}
                >
                  Create a CPN account
                </Link>
              </Button>
              <Button asChild variant="ghost-dark">
                <Link
                  to="/signin"
                  state={{ from: { pathname: `/profile/${handle}/claim` } }}
                >
                  Already have an account? Sign in
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-4">
                <Avatar
                  name={profile.display_name}
                  src={profile.avatar_url}
                  size="lg"
                />
                <div>
                  <CardTitle>{profile.display_name}</CardTitle>
                  {profile.handle && (
                    <p className="mt-0.5 text-sm text-muted">@{profile.handle}</p>
                  )}
                </div>
              </div>
              <CardDescription className="mt-4">
                Submit your LinkedIn or Facebook profile for admin review. Once approved, you'll receive a{' '}
                <strong>Verified</strong> badge on your profile.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {profile.legacy_post_url && (
                <a
                  href={profile.legacy_post_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mb-6 flex items-center gap-2 rounded-xl border border-border bg-cream-warm/60 px-4 py-3 text-sm text-navy hover:bg-cream-warm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40"
                >
                  <ExternalLink
                    className="h-4 w-4 shrink-0 text-gold-dark"
                    aria-hidden="true"
                  />
                  View original Facebook intro post
                </a>
              )}
              <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                className="flex flex-col gap-5"
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="linkedin_url">
                    LinkedIn or Facebook URL <span className="text-destructive" aria-hidden="true">*</span>
                  </Label>
                  <Input
                    id="linkedin_url"
                    type="url"
                    placeholder="https://linkedin.com/in/… or https://facebook.com/…"
                    aria-invalid={!!errors.linkedin_url}
                    {...register('linkedin_url')}
                  />
                  {errors.linkedin_url && (
                    <p role="alert" aria-live="polite" className="text-xs text-destructive">
                      {errors.linkedin_url.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="note">
                    Note to admin <span className="text-muted">(optional)</span>
                  </Label>
                  <Textarea
                    id="note"
                    placeholder="Anything that will help us verify your identity…"
                    rows={3}
                    aria-invalid={!!errors.note}
                    {...register('note')}
                  />
                  {errors.note && (
                    <p role="alert" aria-live="polite" className="text-xs text-destructive">
                      {errors.note.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 accent-gold focus-visible:ring-2 focus-visible:ring-gold/40"
                      aria-invalid={!!errors.confirmed}
                      {...register('confirmed')}
                    />
                    <span className="text-sm text-navy">
                      I confirm that the Facebook post above is my original CPN introduction,
                      and I am <strong>{profile.display_name}</strong>.
                    </span>
                  </label>
                  {errors.confirmed && (
                    <p role="alert" aria-live="polite" className="text-xs text-destructive">
                      {errors.confirmed.message}
                    </p>
                  )}
                </div>

                {submitError && (
                  <p
                    role="alert"
                    aria-live="polite"
                    className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive"
                  >
                    {submitError}
                  </p>
                )}

                <div className="flex items-center gap-2 rounded-xl bg-gold/10 px-4 py-3 text-xs text-navy">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-gold-dark" aria-hidden="true" />
                  Admin will review your LinkedIn and the original intro post. Typically 1–3 business days.
                </div>

                <Button
                  type="submit"
                  variant="gold"
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting…' : 'Request verification'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <AppHeader />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
